import { PTS_COIN, PTS_PIPE } from './api';
import { sound } from './sound';
import { t } from '../../i18n/i18n';

export const W = 540;
const HUD_H = 76;
const GROUND_H = 80;
const PLAY_TOP = HUD_H;

const GRAVITY = 1040;
const JUMP_V = -410;
const MAX_DT = 0.034;
const BIRD_X = 130;
const PIPE_W = 72;
const PIPE_GAP = 230;
const BASE_SPD = 210;
const MAX_SPD = 360;
const ACCEL = 8;
const PIPE_SPACING = 390;
const MIN_TOP_H = 100;
const HIT_R = 10;
const BANNER_FRAMES = 108;

const PIXEL_FONT = '"Press Start 2P", "Courier New", monospace';

const C = {
  bg: '#060C18', hud: '#040912', hudLine: '#1A3A6E', white: '#FFFFFF', offW: '#D1E0F5',
  green: '#166534', greenLt: '#22C55E', greenHi: '#4ADE80', greenDk: '#14532D',
  red: '#991B1B', redLt: '#EF4444', redHi: '#FCA5A5', redDk: '#7F1D1D',
  amberLt: '#FCD34D', orangeHi: '#FB923C',
  gndTop: '#0F2340', gndMid: '#0A1A30', gndBot: '#050E1C', gndDash: '#163256', ink: '#010408',
};

const OWL = [
  '....KKKKKK..',
  '...KKKKKKKK.',
  '..KKKKKKKKKK',
  '..WWWWWWWW..',
  '.WWWWWBBWW..',
  'GWWWWWBEWWOO',
  'GGWWWWWWWWO.',
  '.GWWWWWWWW..',
  '..GGWWWWWG..',
  '...GGGGGG...',
  '....O..O....',
];
const OWL_COLORS = { K: '#27324D', W: '#F4F8FF', G: '#B8C4D8', B: '#60A5FA', E: '#0B1220', O: '#F59E0B' };

const COIN = [
  '..DDDD..',
  '.DAAAAD.',
  'DALLWAAD',
  'DALWAAAD',
  'DAAWAAAD',
  'DAAAAAAD',
  '.DAAAAD.',
  '..DDDD..',
];
const GIFT = [
  '..RR...RR..',
  '.R..R.R..R.',
  '..RRRRRRR..',
  'LLLLLRLLLLL',
  'LLLLLRLLLLL',
  'DDDDDRDDDDD',
  '.AAAARAAAA.',
  '.AAAARAAAA.',
  '.AAAARAAAA.',
  '.DDDDRDDDD.',
];
const GIFT_COLORS = { R: '#EF4444', L: '#FCD34D', A: '#F59E0B', D: '#B45309' };

const COIN_COLORS = { D: '#8A4A00', A: '#F59E0B', L: '#FCD34D', W: '#FFFFFF' };

function spriteFrom(rows, colors, scale) {
  const c = document.createElement('canvas');
  c.width = rows[0].length * scale;
  c.height = rows.length * scale;
  const g = c.getContext('2d');
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (colors[ch]) {
        g.fillStyle = colors[ch];
        g.fillRect(x * scale, y * scale, scale, scale);
      }
    })
  );
  return c;
}

export const giftDataUrl = () => spriteFrom(GIFT, GIFT_COLORS, 6).toDataURL();
export const owlDataUrl = () => spriteFrom(OWL, OWL_COLORS, 6).toDataURL();

function skylineFrom() {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = 260;
  const g = c.getContext('2d');
  for (let x = 0, i = 0; x < W; i += 1) {
    const w = 34 + ((i * 29) % 30);
    const h = 70 + ((i * 53) % 150);
    g.fillStyle = i % 2 ? '#0B1830' : '#0E2040';
    g.fillRect(x, c.height - h, w, h);
    g.fillStyle = '#1A3A6E';
    for (let wy = c.height - h + 10; wy < c.height - 8; wy += 16) {
      for (let wx = x + 6; wx < x + w - 8; wx += 12) if ((wx * wy + i) % 5 > 1) g.fillRect(wx, wy, 4, 6);
    }
    x += w + 2;
  }
  return c;
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const money = (v) => `$${Math.max(0, Math.round(v)).toLocaleString('en-US')}`;

export function createEngine(canvas, { height: H, getRank, onGameOver }) {
  const PLAY_BOT = H - GROUND_H;
  const MAX_TOP_H = PLAY_BOT - PIPE_GAP - 100;
  const ctx = canvas.getContext('2d');
  canvas.width = W;
  canvas.height = H;
  ctx.imageSmoothingEnabled = false;

  const owl = spriteFrom(OWL, OWL_COLORS, 6);
  const coin = spriteFrom(COIN, COIN_COLORS, 5);
  const skyline = skylineFrom();
  const logo = new Image();
  logo.src = '/icons/brand-mark.svg';
  const stars = Array.from({ length: 48 }, (_, i) => ({
    x: (i * 97 + 41) % W,
    y: PLAY_TOP + ((i * 53 + 19) % (PLAY_BOT - PLAY_TOP)),
    r: i % 4 === 0 ? 2 : 1,
    a: 0.3 + (i % 5) * 0.08,
  }));

  const s = {
    status: 'idle', bird: { y: 0, vy: 0, rot: 0 }, pipes: [], pops: [],
    score: 0, pipesPassed: 0, coins: 0, frame: 0, elapsed: 0, groundOff: 0,
    prevBest: 0, banner: 0, bannerShown: false, startedAt: 0,
  };

  let raf = 0;
  let last = 0;

  const makePipe = (x) => {
    const topH = MIN_TOP_H + Math.random() * (MAX_TOP_H - MIN_TOP_H);
    return { x, topH, green: Math.random() > 0.5, passed: false, coin: { y: topH + PIPE_GAP / 2, collected: false } };
  };

  const reset = (prevBest) => {
    Object.assign(s, {
      bird: { y: (PLAY_TOP + PLAY_BOT) / 2, vy: 0, rot: 0 },
      pipes: [makePipe(W + 120), makePipe(W + 120 + PIPE_SPACING)],
      pops: [], score: 0, pipesPassed: 0, coins: 0, frame: 0, elapsed: 0, groundOff: 0,
      prevBest, banner: 0, bannerShown: false, startedAt: performance.now(),
    });
  };

  const addScore = (points) => {
    s.score += points;
    if (!s.bannerShown && s.prevBest > 0 && s.score > s.prevBest) {
      s.bannerShown = true;
      s.banner = BANNER_FRAMES;
      sound.record();
      for (let i = 0; i < 22; i += 1) {
        const side = i % 2 ? 1 : -1;
        s.pops.push({
          kind: 'spark', x: W / 2 + side * (74 + Math.random() * 86), y: PLAY_TOP + (PLAY_BOT - PLAY_TOP) * 0.4 - 22 + Math.random() * 76,
          vx: side * (22 + Math.random() * 38), vy: -26 + Math.random() * 52, life: 22 + Math.random() * 22,
        });
      }
    }
  };

  const die = () => {
    s.status = 'dead';
    sound.hit();
    onGameOver({
      score: s.score, pipes: s.pipesPassed, coins: s.coins,
      durationMs: Math.round(performance.now() - s.startedAt), playedAt: new Date().toISOString(),
    });
  };

  const flap = () => {
    if (s.status !== 'playing') return;
    s.bird.vy = JUMP_V;
    s.bird.rot = -0.4;
    sound.flap();
  };

  const update = (dt) => {
    s.frame += 1;
    if (s.status === 'paused' || s.status === 'dead') return;
    const cdt = Math.min(dt, MAX_DT);

    s.pops = s.pops
      .map((p) => ({ ...p, x: p.x + (p.vx || 0) * cdt, y: p.y + (p.vy || 0) * cdt, life: p.life - 1 }))
      .filter((p) => p.life > 0);
    if (s.banner > 0) s.banner -= 1;

    if (s.status === 'idle') {
      s.bird.y = (PLAY_TOP + PLAY_BOT) / 2 - 30 + Math.sin(s.frame * 0.048) * 16;
      s.bird.rot = Math.sin(s.frame * 0.048) * 0.12;
      s.groundOff += BASE_SPD * cdt;
      return;
    }

    s.elapsed += cdt;
    const speed = Math.min(BASE_SPD + s.elapsed * ACCEL, MAX_SPD);
    s.bird.vy += GRAVITY * cdt;
    s.bird.y += s.bird.vy * cdt;
    s.bird.rot = clamp(s.bird.vy / 520, -0.55, 1);
    s.groundOff += speed * cdt;

    for (const pipe of s.pipes) {
      pipe.x -= speed * cdt;
      if (!pipe.passed && pipe.x + PIPE_W < BIRD_X) {
        pipe.passed = true;
        s.pipesPassed += 1;
        sound.pass();
        addScore(PTS_PIPE);
      }
      const cx = pipe.x + PIPE_W / 2;
      if (!pipe.coin.collected && Math.abs(cx - BIRD_X) < 22 && Math.abs(pipe.coin.y - s.bird.y) < 22) {
        pipe.coin.collected = true;
        s.coins += 1;
        addScore(PTS_COIN);
        s.pops.push({ x: cx, y: pipe.coin.y, life: 44 });
        sound.coin();
      }
    }

    s.pipes = s.pipes.filter((p) => p.x > -PIPE_W - 20);
    const rightmost = Math.max(...s.pipes.map((p) => p.x), -Infinity);
    if (rightmost < W + 40) s.pipes.push(makePipe(Math.max(W + 40, rightmost + PIPE_SPACING)));

    const top = s.bird.y - HIT_R;
    const bottom = s.bird.y + HIT_R;
    if (top < PLAY_TOP + 2 || bottom >= PLAY_BOT) return die();
    const hit = s.pipes.some(
      (p) => BIRD_X + HIT_R > p.x - 5 && BIRD_X - HIT_R < p.x + PIPE_W + 5 && (top < p.topH + 16 || bottom > p.topH + PIPE_GAP - 14)
    );
    if (hit) die();
  };

  const rect = (color, x, y, w, h) => {
    ctx.fillStyle = color;
    ctx.fillRect(x | 0, y | 0, w | 0, h | 0);
  };

  const text = (str, x, y, font, color, align = 'center') => {
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.fillText(str, x, y);
  };

  const drawPipe = (x, y0, y1, green, capAtTop) => {
    const fill = green ? C.green : C.red;
    const lt = green ? C.greenLt : C.redLt;
    const hi = green ? C.greenHi : C.redHi;
    const dk = green ? C.greenDk : C.redDk;
    const h = y1 - y0;
    if (h <= 0) return;
    rect(fill, x, y0, PIPE_W, h);
    rect(lt, x + 2, y0, 6, h);
    rect(dk, x + PIPE_W - 8, y0, 8, h);
    ctx.strokeStyle = C.ink;
    ctx.lineWidth = 3;
    ctx.strokeRect(x, y0, PIPE_W, h);
    const capY = capAtTop ? y0 : y1 - 18;
    rect(lt, x - 6, capY, PIPE_W + 12, 18);
    rect(hi, x - 6, capY, PIPE_W + 12, 5);
    rect(dk, x - 6, capY + 12, PIPE_W + 12, 6);
    ctx.lineWidth = 2.5;
    ctx.strokeRect(x - 6, capY, PIPE_W + 12, 18);
  };

  const drawBackground = () => {
    rect(C.bg, 0, PLAY_TOP, W, PLAY_BOT - PLAY_TOP);
    ctx.globalAlpha = 0.74;
    ctx.drawImage(skyline, 0, PLAY_BOT - skyline.height + 36);
    ctx.globalAlpha = 0.86;
    ctx.fillStyle = '#D1E0F5';
    ctx.beginPath();
    ctx.arc(W - 130, PLAY_TOP + 84, 34, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = C.bg;
    ctx.beginPath();
    ctx.arc(W - 118, PLAY_TOP + 76, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    const drift = (s.groundOff * 0.16) % (W + 220);
    [[22, 126, 148], [288, 236, 104], [408, 354, 126]].forEach(([cx, cy, cw]) => {
      let x = cx - drift;
      while (x < -220) x += W + 220;
      ctx.globalAlpha = 0.16;
      rect(C.offW, x, cy, cw, 14);
      rect(C.offW, x + cw * 0.15, cy - 12, cw * 0.5, 14);
      ctx.globalAlpha = 1;
    });
    for (const star of stars) {
      ctx.fillStyle = `rgba(139,175,212,${star.a + 0.15 * Math.sin(s.frame * 0.04 + star.x)})`;
      ctx.fillRect(star.x | 0, star.y | 0, star.r, star.r);
    }
  };

  const drawGround = () => {
    rect(C.gndTop, 0, PLAY_BOT, W, 10);
    rect(C.gndMid, 0, PLAY_BOT + 10, W, 28);
    rect(C.gndBot, 0, PLAY_BOT + 38, W, GROUND_H - 38);
    ctx.fillStyle = C.gndDash;
    for (let x = -(s.groundOff % 86); x < W; x += 86) ctx.fillRect(x | 0, PLAY_BOT + 22, 34, 4);
    rect(C.ink, 0, PLAY_BOT + 60, W, GROUND_H - 60);
  };

  const drawHud = () => {
    rect(C.hud, 0, 0, W, HUD_H);
    rect(C.hudLine, 0, HUD_H - 2, W, 2);
    if (logo.complete && logo.naturalWidth) ctx.drawImage(logo, 20, HUD_H / 2 - 20, 40, 40);
    text('ALPHA', 70, HUD_H / 2 - 11, `10px ${PIXEL_FONT}`, C.offW, 'left');
    text('CAPITAL', 70, HUD_H / 2 + 11, `10px ${PIXEL_FONT}`, '#7fb7ff', 'left');

    const rank = getRank(s.score);
    if (rank && s.status !== 'idle') {
      text(t('CURRENT POSITION'), 232, HUD_H / 2 - 12, `10px ${PIXEL_FONT}`, 'rgba(209,224,245,0.62)');
      text(`#${rank}`, 232, HUD_H / 2 + 15, `22px ${PIXEL_FONT}`, C.white);
    }
    const score = money(s.score);
    text(score, W - 29, HUD_H / 2 + 3, `24px ${PIXEL_FONT}`, 'rgba(0,0,0,0.75)', 'right');
    text(score, W - 32, HUD_H / 2, `24px ${PIXEL_FONT}`, C.white, 'right');
  };

  const drawBanner = () => {
    if (s.banner <= 0) return;
    const fade = Math.min(1, s.banner / 20, (BANNER_FRAMES - s.banner) / 12);
    const y = PLAY_TOP + (PLAY_BOT - PLAY_TOP) * 0.4;
    ctx.globalAlpha = fade;
    rect('rgba(4,9,18,0.68)', W / 2 - 186, y - 62, 372, 124);
    text(t('NEW RECORD'), W / 2, y - 20, `18px ${PIXEL_FONT}`, C.amberLt);
    text(money(s.score), W / 2, y + 28, `30px ${PIXEL_FONT}`, C.white);
    ctx.globalAlpha = 1;
  };

  const drawPrompt = () => {
    if (s.status === 'paused') {
      rect('rgba(4,9,18,0.78)', W / 2 - 150, H / 2 - 48, 300, 96);
      text(t('PAUSED'), W / 2, H / 2 - 12, `20px ${PIXEL_FONT}`, C.white);
      text(t('P TO RESUME'), W / 2, H / 2 + 22, `10px ${PIXEL_FONT}`, C.offW);
    }
  };

  const render = () => {
    ctx.clearRect(0, 0, W, H);
    drawBackground();
    for (const p of s.pipes) {
      drawPipe(p.x, 0, p.topH, p.green, false);
      drawPipe(p.x, p.topH + PIPE_GAP, PLAY_BOT, !p.green, true);
      if (!p.coin.collected) {
        ctx.save();
        ctx.translate(p.x + PIPE_W / 2, p.coin.y);
        ctx.scale(0.2 + Math.abs(Math.sin(s.frame * 0.1)) * 0.8, 1);
        ctx.drawImage(coin, -coin.width / 2, -coin.height / 2);
        ctx.restore();
      }
    }
    ctx.save();
    ctx.translate(BIRD_X, Math.round(s.bird.y));
    ctx.rotate(s.bird.rot);
    ctx.drawImage(owl, -owl.width / 2, -owl.height / 2);
    ctx.restore();
    for (const p of s.pops) {
      if (p.kind === 'spark') {
        ctx.globalAlpha = clamp(p.life / 24, 0, 1);
        rect(p.life % 2 > 1 ? C.orangeHi : C.amberLt, p.x, p.y, 6, 6);
        ctx.globalAlpha = 1;
      } else {
        const t = p.life / 44;
        text(`+$${PTS_COIN}`, p.x, p.y - (1 - t) * 36, `9px ${PIXEL_FONT}`, `rgba(252,211,77,${t})`);
      }
    }
    drawGround();
    drawHud();
    drawBanner();
    drawPrompt();
  };

  const loop = (now) => {
    update((now - last) / 1000 || 0);
    last = now;
    render();
    raf = requestAnimationFrame(loop);
  };

  const togglePause = () => {
    if (s.status === 'playing') {
      s.status = 'paused';
      sound.musicPause();
    } else if (s.status === 'paused') {
      s.status = 'playing';
      sound.musicResume();
    }
  };

  const onKey = (e) => {
    if (e.code === 'KeyP' && !(e.target instanceof HTMLInputElement)) togglePause();
    else if ((e.code === 'Space' || e.code === 'ArrowUp') && s.status === 'playing') {
      e.preventDefault();
      flap();
    }
  };
  const onPointer = (e) => {
    e.preventDefault();
    flap();
  };
  const onBlur = () => s.status === 'playing' && togglePause();

  canvas.addEventListener('pointerdown', onPointer);
  window.addEventListener('keydown', onKey);
  window.addEventListener('blur', onBlur);
  reset(0);
  s.status = 'idle';
  raf = requestAnimationFrame(loop);

  return {
    start(prevBest) {
      reset(prevBest);
      s.status = 'playing';
      s.bird.vy = JUMP_V;
      sound.flap();
      sound.musicStart();
    },
    destroy() {
      sound.musicStop();
      cancelAnimationFrame(raf);
      canvas.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
    },
  };
}
